import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Download, FileBarChart, CalendarRange, User as UserIcon, TableProperties } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Reports() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const canPickCollector = isAdmin || user?.can_view_all_reports;
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString().slice(0, 10);
    const monthAhead = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);

    const [collectors, setCollectors] = useState([]);
    const [activity, setActivity] = useState({ from: monthAgo, to: today, collector_id: "" });
    const [promised, setPromised] = useState({ from: today, to: monthAhead, collector_id: "" });
    const yearStart = today.slice(0, 4) + "-01-01";
    const yearEnd = today.slice(0, 4) + "-12-31";
    const [summary, setSummary] = useState({ from: yearStart, to: yearEnd });
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        if (canPickCollector) {
            api.get("/users").then((r) => setCollectors(r.data.filter((u) => u.role === "collector"))).catch(() => {});
        }
    }, [canPickCollector]);

    async function downloadXlsx(url, filename, key) {
        setDownloading(key);
        try {
            const res = await api.get(url, { responseType: "blob" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([res.data]));
            link.download = filename;
            link.click();
            window.URL.revokeObjectURL(link.href);
            toast.success("Excel indirildi");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "İndirme başarısız");
        } finally {
            setDownloading(null);
        }
    }

    function buildQs(state) {
        const p = new URLSearchParams();
        if (state.from) p.set("from_date", state.from);
        if (state.to) p.set("to_date", state.to);
        if (state.collector_id) p.set("collector_id", state.collector_id);
        const s = p.toString();
        return s ? "?" + s : "";
    }

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6">
                <div className="overline">Bölüm 06</div>
                <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">Raporlar</h1>
                <p className="text-sm text-neutral-500 mt-3 max-w-3xl">
                    Tahsilat aktivite raporları ve ödeme sözü tahminlerini Excel olarak indirin.
                    {!canPickCollector && (
                        <span className="ml-2 text-brand-amber font-medium">
                            Yalnızca kendi görüşmelerinizi görebilirsiniz. Diğer tahsildarları görmek için yöneticiden yetki isteyin.
                        </span>
                    )}
                </p>
            </header>

            <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activity Report */}
                <div className="border border-black/15 p-6" data-testid="report-activity-card">
                    <div className="flex items-center gap-2 mb-1">
                        <FileBarChart size={16} className="text-brand-blue" />
                        <div className="overline">Rapor 01</div>
                    </div>
                    <h2 className="font-display text-2xl font-bold tracking-tight">Görüşme Aktivite Raporu</h2>
                    <p className="text-xs text-neutral-500 mt-2 mb-5">
                        Belirtilen aralıkta yapılan tüm görüşmeler: kim, ne zaman, kimle, sonuç, ödeme sözü ve not.
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <label className="block">
                            <span className="overline block mb-1 flex items-center gap-1"><CalendarRange size={11} /> Başlangıç</span>
                            <input
                                data-testid="activity-from"
                                type="date"
                                value={activity.from}
                                onChange={(e) => setActivity({ ...activity, from: e.target.value })}
                                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            />
                        </label>
                        <label className="block">
                            <span className="overline block mb-1 flex items-center gap-1"><CalendarRange size={11} /> Bitiş</span>
                            <input
                                data-testid="activity-to"
                                type="date"
                                value={activity.to}
                                onChange={(e) => setActivity({ ...activity, to: e.target.value })}
                                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            />
                        </label>
                        {canPickCollector && (
                            <label className="block col-span-2">
                                <span className="overline block mb-1 flex items-center gap-1"><UserIcon size={11} /> Tahsildar</span>
                                <select
                                    data-testid="activity-collector"
                                    value={activity.collector_id}
                                    onChange={(e) => setActivity({ ...activity, collector_id: e.target.value })}
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue"
                                >
                                    <option value="">Tümü</option>
                                    {collectors.map((u) => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>

                    <button
                        data-testid="download-activity"
                        onClick={() => downloadXlsx(
                            "/reports/collections-activity.xlsx" + buildQs(activity),
                            `gorusme_raporu_${activity.from}_${activity.to}.xlsx`,
                            "activity"
                        )}
                        disabled={downloading === "activity"}
                        className="btn-sharp w-full justify-center"
                    >
                        <Download size={13} /> {downloading === "activity" ? "İndiriliyor…" : "Excel İndir"}
                    </button>
                </div>

                {/* Promised Payments Report */}
                <div className="border border-black/15 p-6" data-testid="report-promised-card">
                    <div className="flex items-center gap-2 mb-1">
                        <FileBarChart size={16} className="text-brand-green" />
                        <div className="overline">Rapor 02</div>
                    </div>
                    <h2 className="font-display text-2xl font-bold tracking-tight">Ödeme Sözü Tahmin Raporu</h2>
                    <p className="text-xs text-neutral-500 mt-2 mb-5">
                        Belirtilen aralıkta hangi gün ne kadar ödeme geleceğini gösterir. Ayrı sayfada günlük özet toplamı bulunur.
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <label className="block">
                            <span className="overline block mb-1 flex items-center gap-1"><CalendarRange size={11} /> Başlangıç</span>
                            <input
                                data-testid="promised-from"
                                type="date"
                                value={promised.from}
                                onChange={(e) => setPromised({ ...promised, from: e.target.value })}
                                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            />
                        </label>
                        <label className="block">
                            <span className="overline block mb-1 flex items-center gap-1"><CalendarRange size={11} /> Bitiş</span>
                            <input
                                data-testid="promised-to"
                                type="date"
                                value={promised.to}
                                onChange={(e) => setPromised({ ...promised, to: e.target.value })}
                                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            />
                        </label>
                        {canPickCollector && (
                            <label className="block col-span-2">
                                <span className="overline block mb-1 flex items-center gap-1"><UserIcon size={11} /> Tahsildar</span>
                                <select
                                    data-testid="promised-collector"
                                    value={promised.collector_id}
                                    onChange={(e) => setPromised({ ...promised, collector_id: e.target.value })}
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue"
                                >
                                    <option value="">Tümü</option>
                                    {collectors.map((u) => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>

                    <button
                        data-testid="download-promised"
                        onClick={() => downloadXlsx(
                            "/reports/promised-payments.xlsx" + buildQs(promised),
                            `odeme_sozleri_${promised.from}_${promised.to}.xlsx`,
                            "promised"
                        )}
                        disabled={downloading === "promised"}
                        className="btn-sharp w-full justify-center"
                    >
                        <Download size={13} /> {downloading === "promised" ? "İndiriliyor…" : "Excel İndir"}
                    </button>
                </div>

                {/* Tahsilat Raporu (multi-column) */}
                <div className="border border-black/15 p-6 lg:col-span-2" data-testid="report-summary-card">
                    <div className="flex items-center gap-2 mb-1">
                        <TableProperties size={16} className="text-brand-amber" />
                        <div className="overline">Rapor 03</div>
                    </div>
                    <h2 className="font-display text-2xl font-bold tracking-tight">Tahsilat Raporu (Aylık Dağılım)</h2>
                    <p className="text-xs text-neutral-500 mt-2 mb-5 max-w-3xl">
                        Seçilen tarih aralığı içindeki her müşteri için: sol tarafta <strong>ay bazlı fatura toplamları</strong> (bizim DB'den),
                        sağ tarafta <strong>açık faturaların vade ayına göre dağılımı</strong> (Açık Faturalar ekranından yüklediğiniz Excel'den).
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <label className="block">
                            <span className="overline block mb-1 flex items-center gap-1"><CalendarRange size={11} /> Başlangıç</span>
                            <input
                                data-testid="summary-from"
                                type="date"
                                value={summary.from}
                                onChange={(e) => setSummary({ ...summary, from: e.target.value })}
                                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            />
                        </label>
                        <label className="block">
                            <span className="overline block mb-1 flex items-center gap-1"><CalendarRange size={11} /> Bitiş</span>
                            <input
                                data-testid="summary-to"
                                type="date"
                                value={summary.to}
                                onChange={(e) => setSummary({ ...summary, to: e.target.value })}
                                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                            />
                        </label>
                        <div className="flex items-end">
                            <button
                                data-testid="download-summary"
                                onClick={() => downloadXlsx(
                                    `/reports/collections-summary.xlsx?from_date=${summary.from}&to_date=${summary.to}`,
                                    `tahsilat_raporu_${summary.from}_${summary.to}.xlsx`,
                                    "summary"
                                )}
                                disabled={downloading === "summary"}
                                className="btn-sharp w-full justify-center"
                            >
                                <Download size={13} /> {downloading === "summary" ? "İndiriliyor…" : "Excel İndir"}
                            </button>
                        </div>
                    </div>

                    <p className="text-[11px] font-mono text-neutral-500 leading-relaxed border-t border-black/10 pt-3 mt-2">
                        💡 Açık faturalar tarafını doldurmak için önce <a href="/acik-faturalar" className="text-brand-blue underline">Açık Faturalar</a> ekranından güncel listeyi yükleyin.
                    </p>
                </div>
            </div>
        </div>
    );
}
